import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { FaHistory, FaInfoCircle, FaCoins, FaTrophy, FaRedo } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import "./HeadTailGame.css";

const API_URL = process.env.REACT_APP_API_URL || "";
const BET_OPTIONS = [10, 20, 50, 100, 200, 500, 1000, 5000];
const TAX_RATE = 10;
const PAYOUT_MULTIPLIER = 2;

const money = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

function HeadTailGame() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [wallet, setWallet] = useState(0);
  const [choice, setChoice] = useState("heads");
  const [betAmount, setBetAmount] = useState(100);
  const [gameCode, setGameCode] = useState(null);
  const [phase, setPhase] = useState("idle"); // idle | ready | flipping | result
  const [result, setResult] = useState(null);
  const [winAmount, setWinAmount] = useState(0);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);

  const grossPrize = betAmount * PAYOUT_MULTIPLIER;
  const tax = grossPrize * (TAX_RATE / 100);
  const netPrize = grossPrize - tax;

  const api = useMemo(() => ({
    headers: { Authorization: token },
  }), [token]);

  const loadBalance = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/headtail/active`, api);
      if (res.data?.success) {
        setWallet(Number(res.data.balance || 0));
        if (res.data.active && res.data.data) {
          setGameCode(res.data.data.gameCode);
          setChoice(res.data.data.choice || "heads");
          setBetAmount(Number(res.data.data.betAmount || 0));
          setPhase("ready");
        }
      }
    } catch (error) {
      if (error.response?.status === 401) toast.error("Please login again");
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/headtail/history`, api);
      if (res.data?.success) setHistory(res.data.data || []);
    } catch (error) {
      console.error("HEADTAIL HISTORY:", error);
    }
  };

  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }
    loadBalance();
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startGame = async () => {
    if (playing || phase === "flipping") return;

    const amount = Number(betAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error("Enter a valid bet amount");
      return;
    }
    if (amount > wallet) {
      toast.error("Insufficient balance");
      return;
    }

    setPlaying(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/headtail/start`,
        { choice, betAmount: amount },
        api
      );

      if (!res.data?.success) {
        toast.error(res.data?.message || "Unable to start game");
        return;
      }

      setGameCode(res.data.gameCode);
      setWallet(Number(res.data.balance || 0));
      setBetAmount(amount);
      setResult(null);
      setWinAmount(0);
      setPhase("ready");
      toast.success("Bet placed. Flip the coin!");
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to start game");
    } finally {
      setPlaying(false);
    }
  };

  const finishGame = async () => {
    if (!gameCode || playing || phase === "flipping") return;

    setPlaying(true);
    setPhase("flipping");
    setResult(null);

    try {
      const res = await axios.post(
        `${API_URL}/api/headtail/finish`,
        { gameCode },
        api
      );

      if (!res.data?.success) {
        toast.error(res.data?.message || "Unable to settle game");
        setPhase("ready");
        return;
      }

      // Keep the reveal visually dramatic while the outcome itself is server-generated.
      window.setTimeout(() => {
        const serverResult = String(res.data.result).toLowerCase();
        setResult(serverResult);
        setWinAmount(Number(res.data.winAmount || 0));
        setWallet(Number(res.data.balance || 0));
        setPhase("result");
        loadHistory();
      }, 900);
    } catch (error) {
      setPhase("ready");
      toast.error(error.response?.data?.message || "Unable to settle game");
    } finally {
      window.setTimeout(() => setPlaying(false), 950);
    }
  };

  const resetRound = () => {
    setGameCode(null);
    setResult(null);
    setWinAmount(0);
    setPhase("idle");
  };

  const selectChoice = (value) => {
    if (phase !== "idle" || playing) return;
    setChoice(value);
  };

  return (
    <div className="ht-page">
      <div className="ht-bg-orb ht-orb-one" />
      <div className="ht-bg-orb ht-orb-two" />

      <header className="ht-topbar">
        <button className="ht-back" onClick={() => navigate("/")}>←</button>
        <div className="ht-brand">
          <div className="ht-brand-coin">🪙</div>
          <div>
            <div className="ht-brand-title">HEAD <span>/</span> TAIL</div>
            <div className="ht-brand-subtitle">Instant coin flip</div>
          </div>
        </div>
        <div className="ht-wallet">
          <FaCoins />
          <div>
            <small>Balance</small>
            <strong>{money(wallet)}</strong>
          </div>
        </div>
      </header>

      <main className="ht-container">
        <section className="ht-hero">
          <div>
            <span className="ht-live"><i /> LIVE GAME</span>
            <h1>Pick a side.<br /><span>Trust the flip.</span></h1>
            <p>Choose Heads or Tails, place your stake, and reveal a cryptographically generated result.</p>
          </div>
          <div className="ht-hero-stats">
            <div><strong>2×</strong><span>Gross payout</span></div>
            <div><strong>10%</strong><span>Tax</span></div>
            <div><strong>1.8×</strong><span>Net payout</span></div>
          </div>
        </section>

        <section className="ht-game-grid">
          <div className="ht-main-card">
            <div className="ht-card-head">
              <div>
                <span className="ht-kicker">YOUR PREDICTION</span>
                <h2>Choose your side</h2>
              </div>
              <button className="ht-icon-btn" onClick={() => setShowRules(true)} aria-label="Rules">
                <FaInfoCircle />
              </button>
            </div>

            <div className="ht-choice-row">
              <button
                className={`ht-choice ${choice === "heads" ? "selected" : ""}`}
                onClick={() => selectChoice("heads")}
                disabled={phase !== "idle" || playing}
              >
                <div className="ht-choice-coin heads-coin">
                  <span>H</span>
                </div>
                <div>
                  <strong>HEADS</strong>
                  <small>Front side</small>
                </div>
                {choice === "heads" && <b className="ht-check">✓</b>}
              </button>

              <button
                className={`ht-choice ${choice === "tails" ? "selected" : ""}`}
                onClick={() => selectChoice("tails")}
                disabled={phase !== "idle" || playing}
              >
                <div className="ht-choice-coin tails-coin">
                  <span>T</span>
                </div>
                <div>
                  <strong>TAILS</strong>
                  <small>Back side</small>
                </div>
                {choice === "tails" && <b className="ht-check">✓</b>}
              </button>
            </div>

            <div className="ht-divider"><span>STAKE AMOUNT</span></div>

            <div className="ht-amount-grid">
              {BET_OPTIONS.map((amount) => (
                <button
                  key={amount}
                  className={Number(betAmount) === amount ? "active" : ""}
                  onClick={() => phase === "idle" && !playing && setBetAmount(amount)}
                  disabled={phase !== "idle" || playing}
                >
                  ₹{amount.toLocaleString("en-IN")}
                </button>
              ))}
            </div>

            <div className="ht-custom-amount">
              <span>₹</span>
              <input
                type="number"
                min="1"
                value={betAmount}
                disabled={phase !== "idle" || playing}
                onChange={(e) => setBetAmount(e.target.value)}
                placeholder="Enter amount"
              />
              <span className="ht-available">{money(wallet)} available</span>
            </div>

            <div className="ht-payout">
              <div><span>Stake</span><strong>{money(betAmount)}</strong></div>
              <div><span>Potential net win</span><strong>{money(netPrize)}</strong></div>
            </div>

            {phase === "idle" && (
              <button className="ht-primary-btn" onClick={startGame} disabled={loading || playing}>
                <FaCoins /> PLACE BET & CONTINUE
              </button>
            )}

            {phase === "ready" && (
              <button className="ht-primary-btn ht-flip-btn" onClick={finishGame} disabled={playing}>
                🪙 FLIP THE COIN
              </button>
            )}

            {phase === "flipping" && (
              <div className="ht-flip-stage">
                <div className="ht-coin ht-coin-spinning"><span>H</span></div>
                <strong>FLIPPING...</strong>
                <small>Waiting for the server result</small>
              </div>
            )}

            {phase === "result" && (
              <div className={`ht-result ${result === choice ? "win" : "lose"}`}>
                <div className={`ht-coin ht-final-coin ${result === "tails" ? "tail-face" : ""}`}>
                  <span>{result === "heads" ? "H" : "T"}</span>
                </div>
                <div>
                  <small>RESULT</small>
                  <h3>{result?.toUpperCase()}</h3>
                  {result === choice ? (
                    <p>You won <b>{money(winAmount)}</b></p>
                  ) : (
                    <p>Better luck next round.</p>
                  )}
                </div>
                <button className="ht-next-btn" onClick={resetRound}><FaRedo /> NEXT ROUND</button>
              </div>
            )}
          </div>

          <aside className="ht-side-card">
            <div className="ht-side-title">
              <div><FaHistory /> Recent Results</div>
              <button onClick={() => setShowHistory(true)}>View all</button>
            </div>

            <div className="ht-history-list">
              {history.slice(0, 7).map((item) => (
                <div className="ht-history-item" key={item.id || item.game_code}>
                  <div className={`ht-mini-coin ${item.result === "tails" ? "tail-face" : ""}`}>
                    {item.result === "heads" ? "H" : "T"}
                  </div>
                  <div className="ht-history-info">
                    <strong>{String(item.result || "").toUpperCase()}</strong>
                    <small>{item.game_code}</small>
                  </div>
                  <b className={item.result === item.choice ? "positive" : "negative"}>
                    {item.result === item.choice ? `+${money(item.win_amount)}` : `-${money(item.bet_amount)}`}
                  </b>
                </div>
              ))}
              {!history.length && <div className="ht-empty">No completed flips yet.</div>}
            </div>

            <div className="ht-fair-card">
              <FaTrophy />
              <div><strong>Fair-play result</strong><span>The result is generated on the server, not by the UI.</span></div>
            </div>
          </aside>
        </section>

        <div className="ht-footer-note">
          <span>✓ Secure bet settlement</span>
          <span>✓ Instant wallet update</span>
          <span>✓ Round history</span>
        </div>
      </main>

      {showRules && (
        <div className="ht-modal-backdrop" onClick={() => setShowRules(false)}>
          <div className="ht-modal" onClick={(e) => e.stopPropagation()}>
            <button className="ht-modal-close" onClick={() => setShowRules(false)}>×</button>
            <div className="ht-modal-icon">🪙</div>
            <h2>How to play</h2>
            <ol>
              <li>Choose <b>Heads</b> or <b>Tails</b>.</li>
              <li>Select your stake and tap <b>Place Bet</b>.</li>
              <li>Tap <b>Flip the Coin</b> to reveal the server-generated result.</li>
              <li>If your prediction matches, the net payout is credited after tax.</li>
            </ol>
            <p className="ht-rule-note">This screen never decides the outcome; settlement is performed by the backend.</p>
          </div>
        </div>
      )}

      {showHistory && (
        <div className="ht-modal-backdrop" onClick={() => setShowHistory(false)}>
          <div className="ht-modal ht-history-modal" onClick={(e) => e.stopPropagation()}>
            <button className="ht-modal-close" onClick={() => setShowHistory(false)}>×</button>
            <div className="ht-modal-head"><FaHistory /><div><h2>Game History</h2><span>Last 100 rounds</span></div></div>
            <div className="ht-full-history">
              {history.map((item) => (
                <div className="ht-full-history-row" key={item.id || item.game_code}>
                  <div className={`ht-mini-coin ${item.result === "tails" ? "tail-face" : ""}`}>
                    {item.result === "heads" ? "H" : "T"}
                  </div>
                  <div><strong>{String(item.result || "").toUpperCase()}</strong><small>{item.game_code}</small></div>
                  <div><span>Bet</span><b>{money(item.bet_amount)}</b></div>
                  <div className={item.result === item.choice ? "positive" : "negative"}>
                    <span>{item.result === item.choice ? "WIN" : "LOSS"}</span>
                    <b>{item.result === item.choice ? `+${money(item.win_amount)}` : `-${money(item.bet_amount)}`}</b>
                  </div>
                </div>
              ))}
              {!history.length && <div className="ht-empty">No history available.</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


export default HeadTailGame;